/**
 * El IVA del período, en «Resumen → Ventas» (lo pidió el dueño, 2026-10-20):
 * «total facturado (completo con iva), total sin iva e iva cobrado».
 *
 * Se lee como la suma que es: Total facturado = Sin IVA + IVA. El total es el
 * mismo número que «Vendido» de arriba, ya sin devoluciones; el servidor lo
 * garantiza (`reports/impuestos.py`), para que la pantalla no tenga dos totales
 * del mismo período.
 *
 * Debajo, cada impuesto con su tasa, su base y en cuántas ventas se cobró: es lo
 * que pide el contador, y «en 34 de 50 ventas» deja ver el IVA que no se
 * encendió al cobrar.
 *
 * ⚠️ Con los impuestos apagados en el negocio y nada cobrado, la tarjeta no
 * sale: tres cifras para decir «cero» a quien no cobra IVA es ruido.
 */

import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Equal, Plus } from "lucide-react";
import { Esqueleto, Tarjeta, TituloSeccion, cx } from "@/components/ui";
import type { Rango } from "@/components/FiltroDeFechas";
import { reportes } from "@/lib/endpoints";
import { formatMoney, formatPercent, toNumber, type FormatoMoneda } from "@/lib/money";
import { plural } from "@/lib/texto";
import type { ImpuestoDelPeriodo, ReporteDeImpuestos } from "@/lib/types";
import { Cifra, Signo } from "./CifrasEnCadena";

/** El color de cada impuesto en la barra y en su fila: el mismo en las dos. */
const colorDel = (i: number) => `var(--c-serie-${(i % 6) + 1})`;

/**
 * Si todo lo cobrado es IVA, los rótulos dicen «IVA»; con el IGTF u otro,
 * «impuestos» (al total sin ellos también se le quitó el IGTF).
 */
export function esSoloIva(detalle: ImpuestoDelPeriodo[]): boolean {
  return detalle.length > 0 && detalle.every((d) => d.nombre.trim().toUpperCase().startsWith("IVA"));
}

export function SeccionImpuestos({
  rango,
  formato,
  impuestosActivos,
  enfocar = false,
}: {
  rango: Rango;
  formato: FormatoMoneda;
  /** `taxes_enabled` del negocio: sin él y sin nada cobrado, no se dibuja. */
  impuestosActivos: boolean;
  /**
   * Viniendo de la tarjeta del IVA de Inicio (`?ver=impuestos`): la pantalla
   * baja sola hasta aquí, que está al final de Ventas.
   */
  enfocar?: boolean;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["reporte-impuestos", rango.desde, rango.hasta],
    queryFn: () => reportes.impuestos(rango),
  });

  // Una sola vez: al cambiar de fechas no tiene que volver a saltar.
  const caja = useRef<HTMLDivElement>(null);
  const yaBajo = useRef(false);
  useEffect(() => {
    const el = caja.current;
    if (!enfocar || !data || yaBajo.current || !el) return;
    const bajar = () => el.scrollIntoView({ block: "start" });
    bajar();
    // ⚠️ Lo de arriba (las gráficas, el mapa de calor) llega DESPUÉS y empuja
    // la tarjeta: bajando una sola vez se quedaba a media pantalla. Durante un
    // momento se vuelve a alinear cada vez que la página crece, salvo que la
    // persona ya se haya movido por su cuenta.
    const soltar = () => {
      yaBajo.current = true;
      observador.disconnect();
    };
    const observador = new ResizeObserver(() => {
      if (!yaBajo.current) bajar();
    });
    observador.observe(document.body);
    const fin = window.setTimeout(soltar, 2500);
    window.addEventListener("wheel", soltar, { passive: true });
    window.addEventListener("touchstart", soltar, { passive: true });
    window.addEventListener("keydown", soltar);
    return () => {
      observador.disconnect();
      window.clearTimeout(fin);
      window.removeEventListener("wheel", soltar);
      window.removeEventListener("touchstart", soltar);
      window.removeEventListener("keydown", soltar);
    };
  }, [enfocar, data]);

  if (isLoading) {
    return impuestosActivos ? (
      <Tarjeta>
        <Esqueleto filas={3} />
      </Tarjeta>
    ) : null;
  }
  if (!data) return null;

  const facturado = toNumber(data.total_facturado);
  const impuestos = toNumber(data.impuestos);
  if (impuestos === 0 && !impuestosActivos) return null;

  // Rótulos cortos a propósito: en el celular van dos cajas por fila y uno
  // más largo que «Sin impuestos» se corta.
  const soloIva = esSoloIva(data.detalle);
  const deLoFacturado = (monto: string) =>
    facturado > 0 ? `${formatPercent((toNumber(monto) / facturado) * 100)} de lo facturado` : undefined;

  return (
    // `scroll-mt`: que al bajar sola no quede pegada al borde de arriba ni,
    // en el celular, debajo de la barra fija con el nombre del negocio.
    <div ref={caja} id="impuestos-del-periodo" className="scroll-mt-20 lg:scroll-mt-4">
      <Tarjeta>
        <TituloSeccion
          titulo={soloIva ? "IVA del período" : "Impuestos del período"}
          descripcion="Lo facturado, separado en la base sin impuestos y lo cobrado por cada impuesto. Ya descontadas las devoluciones."
        />

        {/*
          En el celular, el total arriba a lo ancho y las dos partes debajo, una al
          lado de la otra; los signos solo caben en pantallas anchas.
        */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)]">
          <div className="col-span-2 flex sm:col-span-1">
            <Cifra
              rotulo="Total facturado"
              valor={formatMoney(data.total_facturado, formato)}
              pie={`${plural(data.ventas, "venta", "ventas")}, con impuestos`}
              destacada
            />
          </div>
          <Signo icono={<Equal size={14} />} />
          <div className="flex">
            <Cifra
              rotulo={soloIva ? "Total sin IVA" : "Sin impuestos"}
              valor={formatMoney(data.sin_impuestos, formato)}
              pie={deLoFacturado(data.sin_impuestos)}
            />
          </div>
          <Signo icono={<Plus size={14} />} />
          <div className="flex">
            <Cifra
              rotulo={soloIva ? "IVA cobrado" : "Impuestos"}
              valor={formatMoney(data.impuestos, formato)}
              pie={deLoFacturado(data.impuestos)}
            />
          </div>
        </div>

        {data.detalle.length === 0 ? (
          <p className="mt-4 text-sm text-muted">En este período no se cobró ningún impuesto.</p>
        ) : (
          <DesgloseDeImpuestos data={data} formato={formato} enDosColumnas className="mt-5" />
        )}

        {toNumber(data.impuesto_devuelto) > 0 && (
          <p className="mt-2 text-xs text-muted">
            Se restaron {formatMoney(data.impuesto_devuelto, formato)} de impuestos por las devoluciones
            del período.
          </p>
        )}
      </Tarjeta>
    </div>
  );
}

/**
 * La barra de lo facturado y cada impuesto con su tasa, su base y en cuántas
 * ventas se cobró. La usan el Resumen y la tarjeta del IVA de Inicio: una sola
 * versión, para que las dos digan lo mismo con los mismos colores.
 */
export function DesgloseDeImpuestos({
  data,
  formato,
  enDosColumnas = false,
  className,
}: {
  data: ReporteDeImpuestos;
  formato: FormatoMoneda;
  /** En el Resumen, a todo lo ancho, el monto quedaba lejísimos de su nombre. */
  enDosColumnas?: boolean;
  className?: string;
}) {
  const facturado = toNumber(data.total_facturado);
  return (
    <div className={className}>
      {/*
        La barra es lo facturado entero: lo gris es la base y cada color, un
        impuesto. Un impuesto pequeño (el IGTF) conserva unos píxeles para
        que no desaparezca de la barra teniendo su fila debajo.
      */}
      <div
        className="flex h-2.5 overflow-hidden rounded-full bg-surface-2"
        role="img"
        aria-label={`De ${formatMoney(data.total_facturado, formato)} facturados, ${formatMoney(
          data.impuestos,
          formato,
        )} son impuestos`}
      >
        <div
          className="h-full"
          style={{
            width: `${facturado > 0 ? (toNumber(data.sin_impuestos) / facturado) * 100 : 0}%`,
            background: "var(--c-line-strong)",
          }}
        />
        {data.detalle.map((d, i) => (
          <div
            key={`${d.nombre}-${d.tasa}-${d.sobre_el_total}`}
            className="h-full border-l-2 border-surface"
            style={{
              width: `${facturado > 0 ? (toNumber(d.monto) / facturado) * 100 : 0}%`,
              minWidth: toNumber(d.monto) > 0 ? 6 : 0,
              background: colorDel(i),
            }}
          />
        ))}
      </div>

      <ul className={cx("mt-3 grid gap-x-8", enDosColumnas && "sm:grid-cols-2")}>
        {data.detalle.map((d, i) => (
          <li
            key={`${d.nombre}-${d.tasa}-${d.sobre_el_total}`}
            className="flex items-center gap-3 border-t border-line py-2.5"
          >
            <span className="size-2.5 shrink-0 rounded-full" style={{ background: colorDel(i) }} aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">
                {d.nombre}
                <span className="ml-1.5 font-normal text-muted">{formatPercent(d.tasa)}</span>
              </p>
              <p className="text-xs text-muted">
                Base imponible <span className="tabular">{formatMoney(d.base, formato)}</span>
                {" · "}
                {d.ventas === data.ventas
                  ? `en ${d.ventas === 1 ? "la venta" : `las ${d.ventas} ventas`}`
                  : `en ${d.ventas} de ${plural(data.ventas, "venta", "ventas")}`}
                {!d.sobre_el_total && " · en el precio de cada producto"}
              </p>
            </div>
            <span className="tabular shrink-0 text-sm font-semibold text-ink">
              {formatMoney(d.monto, formato)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
